import { orderBy, query } from 'firebase/firestore';

import DocumentCollection from './DocumentCollection';
import type Tearsheet from './Tearsheet';
import TearsheetAd, { type TearsheetAdSchema } from './TearsheetAd';

/**
 * Ads Data Store
 *
 * Filtering: Filtering generally works by scanning all ads and collecting all the possible values on a per "property" basis.
 *
 * current - The currently selected ad.
 * filterValues - Values that are currently selected for a specific dynamic data property. This can be multiple.
 * filterProperties - All the collective values that exist in the entire data set for this specific property. If 200 ads use a productImage of 'foo.jpg', and another 100 use 'bar.jpg, this will return an array of ['ball.jpg', 'bar.jpg']
 */

export default class TearsheetAdsManager extends DocumentCollection<
    TearsheetAd,
    TearsheetAdSchema
> {
    // TODO: this should extend Document and use a type variable to pass in child type.
    query = () => query(this.collectionRef, orderBy('index'));

    constructor(parent: Tearsheet) {
        super(parent, TearsheetAd);
        this.becomeObservable();
    }

    get last(): TearsheetAd | undefined {
        return this.children.length > 0
            ? this.children[this.children.length - 1]
            : undefined;
    }

    get first(): TearsheetAd | undefined {
        return this.children[0];
    }

    get current(): TearsheetAd | undefined {
        return this.children.find((ad) => ad.active);
    }

    get next(): TearsheetAd | undefined {
        return this.getRelativeToCurrent(1);
    }

    get previous(): TearsheetAd | undefined {
        return this.getRelativeToCurrent(-1);
    }

    // By position in `children` (intrinsic), which the query orders by
    // nominal index. Undefined at either end, or when no ad is current.
    private getRelativeToCurrent(offset: number): TearsheetAd | undefined {
        const current = this.current;
        if (!current) return undefined;
        return this.children[this.children.indexOf(current) + offset];
    }

    setActiveByNominal1BasedIndex = (nominal1BasedIndex: number) => {
        this.setActiveByNominal0BasedIndex(nominal1BasedIndex - 1);
    };

    private setActiveByNominal0BasedIndex(nominal0BasedIndex: number) {
        // Matches the ad's nominal index (see TearsheetAd), never its
        // position in `children`. A malformed value (NaN, fractional,
        // negative) selects nothing, deliberately. A well-formed one that
        // matches no ad falls back to the first ad, as tearsheet URLs always
        // have.
        if (
            !this.loaded ||
            this.children.length <= 0 ||
            !Number.isSafeInteger(nominal0BasedIndex) ||
            nominal0BasedIndex < 0
        ) {
            return;
        }
        const adToActivate =
            this.children.find(
                (ad) => ad.nominal0BasedIndex === nominal0BasedIndex
            ) ?? this.children[0];
        this.setActiveAd(adToActivate);
    }

    private setActiveAd = (adToActivate: TearsheetAd) => {
        if (!this.loaded) return;
        for (const ad of this.children) {
            if (ad !== adToActivate) {
                ad.deactivate();
            }
        }
        adToActivate.activate();
    };
}
